/**
 * Shared QueryClient instance.
 *
 * Extracted from App.tsx so that non-React code (API service files, utility
 * modules) can import and call queryClient.invalidateQueries() without
 * needing a React hook context.
 *
 * React components should continue to use useQueryClient() from
 * @tanstack/react-query — this module is the fallback for plain TS files.
 */

import { QueryClient } from '@tanstack/react-query';

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,          // 30 seconds fresh
      retry: 2,                    // 2 retries with backoff
      refetchOnWindowFocus: true,  // Refetch when user returns to tab
    },
  },
});

// Clear all cached query data when the user switches organizations.
// Without this, the new org sees stale data from the previous org until
// each query individually refetches (which may not happen if staleTime
// hasn't elapsed). This also covers account switches — the account
// switch flow does a full page reload, but the org change flow does not.
//
// resetQueries(), NOT removeQueries() (bug-registry 0zzz-ee, 2026-09-22):
// removeQueries() deletes the cache entry and silently cancels any
// in-flight fetch (Query.destroy() -> cancel({ silent: true })) but does
// NOT force a refetch for queries that still have a mounted observer. A
// query subscribed (and mid-fetch) at the exact moment this event fires —
// e.g. useSceneLog()'s dashboardQuery, which fires unconditionally with no
// `enabled` guard the instant SceneLogPage mounts — is left holding a
// reference to the destroyed Query object with no further state update
// ever arriving: no error, no data, isLoading stuck true forever, and no
// new network request is ever issued. This is exactly the timing the
// "clears the cache on first org selection (null -> id)" case below
// produces on a fresh session (OrganizationContext's auto-select-the-only-
// org effect resolving mid-render, before a page that mounted with
// currentOrg still null has finished its own first fetch).
// resetQueries() clears the same stale data, but internally calls
// refetchQueries({ type: 'active', ... }) on the matched set as part of
// the SAME call — a query with a live observer gets a genuine new fetch
// dispatched immediately instead of being orphaned. See
// web/src/lib/__tests__/queryClient.orgSwitchInFlight.test.ts, which
// reproduces the exact race and fails against removeQueries().
//
// Conditional on a REAL org-id change (2026-08-04): OrganizationContext
// dispatches this same event on a same-org role sync too (a server-side
// role change with the org id unchanged) — every TanStack-migrated
// Context that reacts to this event was inheriting a wasted full-cache
// wipe + refetch storm on that path (surfaced during the FeaturesContext
// migration, Spec 294 Checkpoint 1). localStorage's 'selectedOrganization'
// is always written BEFORE the event dispatches at every
// OrganizationContext call site (localStorage.setItem/removeItem, then
// dispatchEvent) — re-reading it here reliably detects a genuine id
// change without requiring every dispatch site (present or future) to
// thread a CustomEvent detail through correctly.
const ORG_STORAGE_KEY = 'selectedOrganization';

function getStoredOrgId(): string | null {
  try {
    const stored = localStorage.getItem(ORG_STORAGE_KEY);
    return stored ? (JSON.parse(stored) as { id?: string })?.id ?? null : null;
  } catch {
    return null;
  }
}

if (typeof window !== 'undefined') {
  let lastOrgId = getStoredOrgId();
  window.addEventListener('organizationChanged', () => {
    const newOrgId = getStoredOrgId();
    if (newOrgId !== lastOrgId) {
      if (newOrgId) {
        // A new org: clear AND refetch active queries (0zzz-ee). removeQueries()
        // silently orphaned any fetch in flight, so a page could stay on its
        // skeleton forever. The refetch carries the new org header, because
        // storage is written before this event fires.
        queryClient.resetQueries();
      } else {
        // Org cleared (logout / removed from org): wipe without refetching, as
        // before, since a refetch would run with no org header and surface errors.
        queryClient.removeQueries();
      }
    }
    lastOrgId = newOrgId;
  });
}
